<template>
    <!-- *********************************************
    *                     Header                     *
    ********************************************** -->
    <div v-if="!getMobileAppState" class="header-area">
        <div class="top-area">
            <!-- <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="ticket"
            :label="t('login.ticketBtn')"
            @clicked="navigateByPath(routeList.NON_AUTHENTICATED_TICKETS_PATH)"/> hid for 1º fase, we need to also fix css -->
            <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="saved-login"
            :label="t('login.acessionBtn')"
            @clicked="emit('showAcessionModal')"/>
        </div>
        <header-logo @click="logoClickHandler"/>
    </div>
    <div
    v-else
    class="header-area-mobile"
    :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
        <header-logo/>
        <div class="buttons-wrapper">
            <!-- <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="ticket"
            @clicked="navigateByPath(routeList.NON_AUTHENTICATED_TICKETS_PATH)"/> -->
            <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="saved-login"
            @clicked="emit('showAcessionModal')"/>
        </div>
    </div>
    <!-- *********************************************
    *                      Form                      *
    ********************************************** -->
    <div class="form-wrapper" :class="{ 'mobile': getMobileAppState }">
        <div class="title">{{t('login.formTitle')}}</div>
        <!-- Username Input -->
        <inputs-generic
        id="usernameInput"
        :params="{
            mandatory: true,
            labelText: t('login.acessionInputLabel'),
            placeholder: t('login.acessionInputPlaceholder'),
            maxLength: 15,
            ourMask: {
                parseInputText: value => { return removeAccents(value) },
                validatorFn: value => { return noSpecialCharNoSpace(value) }
            }
        }"
        :inputValue="username"
        @input="emit('updateUsername', $event)"/>
        <!-- Password Input -->
        <inputs-password
        id="passwordInput"
        :params="{
            mandatory: true,
            labelText: t('login.passwordInputLabel'),
            placeholder: t('login.passwordInputPlaceholder'),
            maxLength: 6,
            mask: NUMBERS_ONLY
        }"
        :inputValue="password"
        @input="emit('updatePassword', $event)"
        @onEnter="!!password.length ? emit('loginHandler') : null"/>
        <!-- Options -->
        <div class="login-options-wrapper">
            <selector-checkbox
            :label="t('login.rememberMe')"
            :selected="rememberMe"
            @clicked="emit('updateRememberMe')"/>
            <button-link
            internalClick
            :label="t('login.recoverPassword')"
            @clicked="navigateByPath(routeList.RECOVER_CREDENTIALS_PATH)"/>
        </div>
        <!-- Login Button -->
        <button-primary
        style="width: 100%;"
        icon="arrow-right-full"
        iconPosition="right"
        buttonSize="medium"
        :label="t('login.loginBtn')"
        :disabled="!username.length || !password.length"
        @clicked="emit('loginHandler')"/>
        <!-- New Account Redirect -->
        <div v-if="getClientType !== 'company'" class="redirect-card" @click="navigateByPath(routeList.NON_AUTHENTICATED_JOIN_PATH)">
            <div class="text-wrapper">
                <span class="title">{{ t('login.redirectCardTitle') }}</span>
                <span class="message">{{ t('login.redirectCardMessage') }}</span>
            </div>
            <arrow-right/>
        </div>
    </div>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import InputsGeneric from '@/components/generic/inputs/Generic'
import InputsPassword from '@/components/generic/inputs/Password'
import SelectorCheckbox from '@/components/generic/selectors/checkbox/Checkbox'
import ButtonLink from '@/components/generic/buttons/Link'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ArrowRight from '@/assets/icons/arrow-right-full.svg?inline'

/* Helpers */
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Helpers */
import { NUMBERS_ONLY } from '@/assets/constants/masks'
import { removeAccents, noSpecialCharNoSpace } from '@/assets/scripts/validators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['showAcessionModal', 'updateUsername', 'updatePassword', 'updateRememberMe', 'loginHandler'])

/* mapActions */
const { getClientType, getAppTheme, getMobileAppState, getDeviceInUse } = mapGetters()

/** Props received:
@username { String, Number }
@password { String, Number }
@rememberMe { Boolean }
*/

const props = defineProps({
    username: [String, Number],
    password: [String, Number],
    rememberMe: Boolean
})

const headerLogo = computed(() => {
	const theme = getAppTheme.value + `${getMobileAppState.value ? '' : '-color'}`
	return defineAsyncComponent(() => import('@/assets/images/logo-bai-' + theme + '.svg?inline'))
})

const logoClickHandler = () => {
	if (process.env.VUE_APP_SHOW_DESIGN_SHOWROOM !== 'true') return
	navigateByPath(routeList.DESIGN_SHOWROOM_PATH)
}
</script>

<style lang="scss" scoped>
.header-area {
    width: 100%;
    min-height: 182px;
    padding: 24px;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 48px;

    .top-area {
        width: 100%;

        display: flex;
        flex-direction: row;
        justify-content: flex-end;
    }
}

.header-area-mobile {
    width: 100%;
    height: 88px;

    display: flex;
    flex-shrink: 0;
    flex-direction: row;
    justify-content: space-between;
    position: relative;

    padding: 16px;
    background-image: var(--gradient-horizontal);

    &.mobile-status-bar-android {
        height: calc(88px + var(--android-top-padding)); /*Android status bar*/
        padding-top: calc(16px + var(--android-top-padding));
    }

    &.mobile-status-bar-ios {
        height: calc(88px + constant(safe-area-inset-top)); /* iOS 11.0 */
        height: calc(88px + env(safe-area-inset-top)); /* iOS 11.2 */

        padding-top: calc(16px + constant(safe-area-inset-top));
        padding-top: calc(16px + env(safe-area-inset-top));
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;

        width: 100%;
        height: 16px;

        border-radius: 16px 16px 0 0;
        background-color: var(--secondary-light-4);
    }

    svg {
        width: auto;
        height: 32px;
    }

    .buttons-wrapper {
        display: flex;

        gap: 16px;
    }
}

.form-wrapper {
    width: 400px;
    max-width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    &:not(.mobile) {
        justify-content: center;
    }

    .title {
        width: 100%;

        @include H4-Medium;
        text-align: center;
        color: var(--secondary-dark-3);
    }

    .login-options-wrapper {
        width: 100%;

        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .redirect-card {
        width: 100%;
        height: 72px;
        padding: 0 24px;

        display: flex;
        align-items: center;

        border-radius: 8px;
        background-color: var(--secondary-light-3);

        cursor: pointer;

        .text-wrapper {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .title {
                @include H7-Medium;
                line-height: 16px;
                color: var(--primary);
                text-align: left;
            }

            .message {
                @include H8-Regular;
                line-height: 13px;
                color: var(--secondary-dark-3);
            }
        }

        svg {
            flex-shrink: 0;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                background-color: var(--button-secondary-state-hover);
            }
        }

        &:active {
            background-color: var(--button-secondary-state-press);
        }
    }

    &.mobile {
        padding: 0 16px;

        .title {
            text-align: left;
        }
    }
}
</style>
